import DraftsOutlinedIcon from "@mui/icons-material/DraftsOutlined";
import SearchIcon from "@mui/icons-material/Search";
import { Box, InputAdornment, Menu, MenuItem, TextField, Typography } from "@mui/material";

import BellIconOff from "@/components/Icons/BellIconOff";
import BellIconOn from "@/components/Icons/BellIconOn.tsx";
import type { ChannelsTableState } from "@/components/MiddleTableNavigation/useChannelsTable";
import DataTable from "@/ui/DataTable";
import { zinc } from "@/ui/tokens";

/** Pure channels-roster view: title, search box, virtualized table, and row context menu. */
export default function ChannelsTableView({
  table,
  searchValue,
  onSearchChange,
  onRowClick,
  onRowContextMenu,
  getRowClassName,
  contextMenu,
}: ChannelsTableState) {
  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", p: 2 }}>
      <Typography variant="h6" sx={{ color: "white", fontWeight: 600, mb: 1.5 }}>
        Channels
      </Typography>
      <TextField
        size="small"
        placeholder="Search channels..."
        value={searchValue}
        onChange={(e) => onSearchChange(e.target.value)}
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: zinc[400], fontSize: 20 }} />
              </InputAdornment>
            ),
          },
        }}
        sx={{
          mb: 1.5,
          "& .MuiOutlinedInput-root": {
            bgcolor: zinc[800],
            color: "white",
            "& fieldset": { borderColor: zinc[700] },
            "&:hover fieldset": { borderColor: zinc[600] },
            "&.Mui-focused fieldset": { borderColor: zinc[500] },
          },
          "& .MuiInputBase-input::placeholder": {
            color: zinc[400],
            opacity: 1,
          },
        }}
      />
      <Box sx={{ flex: 1, position: "relative" }}>
        <Box sx={{ position: "absolute", inset: 0 }}>
          <DataTable
            table={table}
            onRowClick={onRowClick}
            onRowContextMenu={onRowContextMenu}
            getRowClassName={getRowClassName}
            pagination={false}
            virtualize
            rowHeight={40}
            headerHeight={32}
            sx={{
              "& .MuiTableContainer-root": { border: "none" },
              "& .MuiTableHead-root .MuiTableCell-root": {
                bgcolor: zinc[800],
                color: zinc[400],
                fontWeight: 600,
                fontSize: "0.7rem",
                textTransform: "uppercase",
                letterSpacing: "0.05em",
              },
              "& .MuiTableCell-root": {
                py: 0.5,
                px: 1.5,
                borderBottom: `1px solid ${zinc[800]}`,
              },
              "& .MuiTableRow-root:hover:not(.channel-row-active)": {
                bgcolor: zinc[800],
              },
              "& .channel-row-active": {
                bgcolor: zinc[600],
                "&:hover": {
                  bgcolor: zinc[600],
                },
              },
            }}
          />
        </Box>
      </Box>
      {contextMenu.target != null && contextMenu.position != null && (
        <Menu open onClose={contextMenu.onClose} anchorReference="anchorPosition" anchorPosition={contextMenu.position}>
          {contextMenu.target.unread && (
            <MenuItem
              disabled={contextMenu.actionDisabled}
              onClick={contextMenu.onMarkAsRead}
              sx={{ display: "flex", gap: 1, alignItems: "center" }}
            >
              <Box sx={{ display: "flex", width: 30, justifyContent: "center", alignItems: "center" }}>
                <DraftsOutlinedIcon sx={{ fontSize: 18 }} />
              </Box>
              <Box>Mark as read</Box>
            </MenuItem>
          )}
          {!contextMenu.target.subscribed && (
            <MenuItem
              disabled={contextMenu.actionDisabled}
              onClick={contextMenu.onSubscribe}
              sx={{ display: "flex", gap: 1, alignItems: "center" }}
            >
              <Box sx={{ display: "flex", width: 30, justifyContent: "center", alignItems: "center" }}>
                <BellIconOn fontSize="18" />
              </Box>
              <Box>Subscribe to notifications</Box>
            </MenuItem>
          )}
          {contextMenu.target.subscribed && (
            <MenuItem
              disabled={contextMenu.actionDisabled}
              onClick={contextMenu.onUnsubscribe}
              sx={{ display: "flex", gap: 1, alignItems: "center" }}
            >
              <Box sx={{ display: "flex", width: 30, justifyContent: "center", alignItems: "center" }}>
                <BellIconOff fontSize="18" />
              </Box>
              <Box>Turn off notifications for this channel</Box>
            </MenuItem>
          )}
        </Menu>
      )}
    </Box>
  );
}
